import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import ResmoMascot from "../components/ResmoMascot";
import FitMatrix from "../components/FitMatrix";
import VerifiedBadge from "../components/ResmoAuthenticitySeal";
import { Building2, Target, Lightbulb, Lock } from "lucide-react";

export default function PublicShare() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/share/${token}`);
        setData(data);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen rm-paper flex items-center justify-center px-4">
        <div className="text-center" data-testid="share-error">
          <Lock size={48} className="mx-auto" />
          <h1 className="font-display text-3xl uppercase mt-3">Link not available</h1>
          <p className="font-mono-rm text-sm mt-2">{error}</p>
          <Link to="/" className="rm-btn rm-btn-primary mt-6 inline-flex">Go to Resmo →</Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen rm-paper flex items-center justify-center">
        <div className="text-center font-mono-rm" data-testid="share-loading">Loading...</div>
      </div>
    );
  }

  const a = data.analysis || {};
  const r = a.result || {};
  const problem = r.problem_assessment || {};
  const company = r.company_insight || {};
  const fit = r.fit_assessment || {};
  const tailored = r.tailored_resume || {};

  return (
    <div className="min-h-screen rm-paper">
      <header className="px-4 md:px-8 py-4 flex items-center justify-between"
              style={{ borderBottom: "4px solid #0F1B2D", background: "rgba(255,242,236,0.92)" }}>
        <Link to="/" className="font-display text-2xl flex items-baseline" style={{ letterSpacing: "-0.02em" }}>
          <span style={{ color: "#FF6A00" }}>R</span><span>esmo</span>
        </Link>
        <Link to="/register" className="rm-btn rm-btn-primary text-xs px-4 py-2" data-testid="share-cta">
          Make your own →
        </Link>
      </header>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <div className="rm-tag" data-testid="share-tag">
            <Lock size={12} /> Read-only fit report{data.shared_by ? ` · shared by ${data.shared_by}` : ""}
          </div>
          {data.verified && <VerifiedBadge verified={data.verified} compact />}
        </div>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl md:text-5xl uppercase leading-none">
              {a.job_title || "Tailored Output"}
            </h1>
            {a.company_name && (
              <div className="font-mono-rm text-sm mt-2 uppercase tracking-widest" style={{ color: "#475569" }}>
                @ {a.company_name}
              </div>
            )}
          </div>
          <ResmoMascot state="satisfied" size={110} />
        </div>

        {data.verified && (
          <div className="mb-6">
            <VerifiedBadge verified={data.verified} />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          <div className="md:col-span-8 rm-card-static p-6 md:p-8">
            <div className="rm-label mb-2"><Target size={12} className="inline mr-1" /> The real problem</div>
            <h2 className="font-display text-xl md:text-2xl uppercase leading-tight mb-2">
              {problem.headline || "—"}
            </h2>
            <p className="text-sm whitespace-pre-line">{problem.details || ""}</p>
          </div>

          <div className="md:col-span-4 rm-card-static p-6 md:p-8" style={{ background: "#FFC145" }}>
            <div className="rm-label mb-2"><Building2 size={12} className="inline mr-1" /> Company tone</div>
            <h3 className="font-display text-xl uppercase">{company.name || "—"}</h3>
            <div className="font-mono-rm text-xs uppercase tracking-widest mt-1">{company.tone || ""}</div>
            <p className="text-sm mt-3">{company.culture_summary || ""}</p>
          </div>

          <div className="md:col-span-12 rm-card-static p-6 md:p-8">
            <div className="rm-label mb-3">Fit Matrix</div>
            <FitMatrix
              matrix={fit.matrix || []}
              overall={fit.overall_score || 0}
              verdict={fit.verdict || ""}
              strengths={fit.strengths || []}
              gaps={fit.gaps || []}
            />
          </div>

          <div className="md:col-span-12 rm-card-static p-6 md:p-8">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                   style={{ background: "#20C1A7", color: "#0F1B2D", border: "3px solid #0F1B2D" }}>
                <Lightbulb size={20} strokeWidth={2.5} />
              </div>
              <div className="rm-label">Tailored resume preview</div>
            </div>
            {tailored.headline && <div className="font-display text-2xl uppercase">{tailored.headline}</div>}
            {tailored.summary && (
              <p className="text-sm mt-3" style={{ color: "#0F1B2D" }}>{tailored.summary}</p>
            )}
            {(tailored.key_skills || []).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {tailored.key_skills.map((s, i) => <span key={i} className="rm-tag">{s}</span>)}
              </div>
            )}
            <div className="mt-6 p-4 rounded-md"
                 style={{ background: "#0F1B2D", color: "white", border: "3px solid #FF6A00" }}>
              <div className="font-display text-lg uppercase">Want the full resume + interview prep?</div>
              <p className="text-sm mt-1" style={{ color: "#CBD5E1" }}>
                Sign up for Resmo and get tailored output + STAR-formatted interview answers in one bite.
              </p>
              <Link to="/register" className="rm-btn rm-btn-secondary mt-3 inline-flex" data-testid="share-bottom-cta">
                Try Resmo free →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
